import type { TripTab } from "@/features/towers/components/TripCardsPanel/tripCardsPanelTypes";
import {
  getDefaultTripListSortOption,
  getTripListSortOptionsForTab,
  type TripListSortByTab,
  type TripListSortOption,
} from "@/features/towers/components/TripCardsPanel/tripListSort";
import { isTripCompletedStatusFilterAllSelected } from "@/features/towers/components/TripCardsPanel/tripVisitStatusFilter";
import type { CalendarDay } from "@/features/towers/utils/tripActiveOnCalendarDay";
import type { VisitStatus } from "@/features/towers/utils/visitStatus";

/**
 * Stable query keys for v1 Trip View trip list state in the URL.
 * Trip id stays in the route path (`/app/towers/trip/:tripId`), not here.
 */
export const TRIP_VIEW_LIST_QUERY_PARAMS = {
  /** List stage tab: IN_TRANSIT | COMPLETED */
  tab: "tab",
  /** Active-on calendar day in viewer TZ, `YYYY-MM-DD` */
  day: "day",
  /** Destination cluster filter (cluster id) */
  dest: "dest",
  /** Trip id search / autocomplete filter text */
  q: "q",
  /** Completed-tab visit status filter (comma-separated) */
  completedStatus: "completedStatus",
  /** In-transit upcoming delivery status filter (comma-separated) */
  upcomingStatus: "upcomingStatus",
  /** Sort option for the in-transit list stage */
  sortInTransit: "sortInTransit",
  /** Sort option for the completed list stage */
  sortCompleted: "sortCompleted",
  /** `1` when the trip cards table strip is collapsed */
  listCollapsed: "listCollapsed",
} as const;

export type TripViewListQueryParamName =
  (typeof TRIP_VIEW_LIST_QUERY_PARAMS)[keyof typeof TRIP_VIEW_LIST_QUERY_PARAMS];

/** All list-related query param names (for detecting share / deep links). */
export const TRIP_VIEW_LIST_QUERY_PARAM_NAMES: readonly TripViewListQueryParamName[] =
  Object.values(TRIP_VIEW_LIST_QUERY_PARAMS);

/** Parse-only; older links used `nextStatus` before rename to `upcomingStatus`. */
const LEGACY_UPCOMING_STATUS_QUERY_PARAM = "nextStatus";

/**
 * Decoded trip list state from URL search params (partial).
 * Merge with fresh-entry or session defaults in later commits.
 */
export type ParsedTripViewListQuery = {
  stageTab?: TripTab;
  activeCalendarDay?: CalendarDay;
  selectedDestinationClusterId?: string | null;
  searchQuery?: string;
  selectedCompletedStatuses?: VisitStatus[];
  selectedNextDeliveryStatuses?: VisitStatus[];
  listSortByByTab?: Partial<TripListSortByTab>;
  isCollapsed?: boolean;
};

/** Full trip list fields written to the URL (from panel or merged state). */
export type TripViewListQuerySerializeInput = {
  stageTab: TripTab;
  activeCalendarDay?: CalendarDay;
  selectedDestinationClusterId: string | null;
  searchQuery: string;
  selectedCompletedStatuses: VisitStatus[];
  selectedNextDeliveryStatuses: VisitStatus[];
  listSortByByTab: TripListSortByTab;
  isCollapsed: boolean;
};

const VISIT_STATUSES = new Set<VisitStatus>([
  "LATE",
  "EARLY",
  "ON_TIME",
  "UNKNOWN",
]);

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseStageTabParam(value: string | null): TripTab | undefined {
  if (value === "IN_TRANSIT" || value === "COMPLETED") {
    return value;
  }
  return undefined;
}

function parseDayQueryParam(value: string | null): CalendarDay | undefined {
  if (value == null || value.trim() === "") {
    return undefined;
  }
  const match = ISO_DATE_PATTERN.exec(value.trim());
  if (match == null) {
    return undefined;
  }
  const year = Number(match[1]);
  const month1 = Number(match[2]);
  const day = Number(match[3]);
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month1) ||
    !Number.isInteger(day) ||
    month1 < 1 ||
    month1 > 12 ||
    day < 1 ||
    day > 31
  ) {
    return undefined;
  }
  return { year, month: month1 - 1, day };
}

function parseVisitStatusCsvParam(
  value: string | null,
): VisitStatus[] | undefined {
  if (value == null) {
    return undefined;
  }
  const tokens = value
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  if (tokens.length === 0) {
    return undefined;
  }
  const filtered = tokens.filter((token): token is VisitStatus =>
    VISIT_STATUSES.has(token as VisitStatus),
  );
  return filtered.length > 0 ? filtered : undefined;
}

function isSortOptionForTab(
  tab: TripTab,
  value: string,
): value is TripListSortOption {
  return (getTripListSortOptionsForTab(tab) as readonly string[]).includes(
    value,
  );
}

function parseListCollapsedParam(value: string | null): boolean | undefined {
  if (value == null) {
    return undefined;
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === "1" || normalized === "true") {
    return true;
  }
  if (normalized === "0" || normalized === "false") {
    return false;
  }
  return undefined;
}

/**
 * Reads v1 trip list state from URL search params. Omitted or invalid params are
 * left unset on the result so callers can merge fresh-entry or session defaults.
 */
export function parseTripViewListStateFromSearchParams(
  searchParams: URLSearchParams,
): ParsedTripViewListQuery {
  const result: ParsedTripViewListQuery = {};

  const stageTab = parseStageTabParam(
    searchParams.get(TRIP_VIEW_LIST_QUERY_PARAMS.tab),
  );
  if (stageTab != null) {
    result.stageTab = stageTab;
  }

  const activeCalendarDay = parseDayQueryParam(
    searchParams.get(TRIP_VIEW_LIST_QUERY_PARAMS.day),
  );
  if (activeCalendarDay != null) {
    result.activeCalendarDay = activeCalendarDay;
  }

  if (searchParams.has(TRIP_VIEW_LIST_QUERY_PARAMS.dest)) {
    const dest = searchParams.get(TRIP_VIEW_LIST_QUERY_PARAMS.dest);
    result.selectedDestinationClusterId =
      dest == null || dest === "" ? null : dest;
  }

  if (searchParams.has(TRIP_VIEW_LIST_QUERY_PARAMS.q)) {
    result.searchQuery = searchParams.get(TRIP_VIEW_LIST_QUERY_PARAMS.q) ?? "";
  }

  const selectedCompletedStatuses = parseVisitStatusCsvParam(
    searchParams.get(TRIP_VIEW_LIST_QUERY_PARAMS.completedStatus),
  );
  if (selectedCompletedStatuses != null) {
    result.selectedCompletedStatuses = selectedCompletedStatuses;
  }

  const selectedNextDeliveryStatuses = parseVisitStatusCsvParam(
    searchParams.get(TRIP_VIEW_LIST_QUERY_PARAMS.upcomingStatus) ??
      searchParams.get(LEGACY_UPCOMING_STATUS_QUERY_PARAM),
  );
  if (selectedNextDeliveryStatuses != null) {
    result.selectedNextDeliveryStatuses = selectedNextDeliveryStatuses;
  }

  const listSortByByTab: Partial<TripListSortByTab> = {};
  const sortInTransitRaw = searchParams.get(
    TRIP_VIEW_LIST_QUERY_PARAMS.sortInTransit,
  );
  if (
    sortInTransitRaw != null &&
    isSortOptionForTab("IN_TRANSIT", sortInTransitRaw)
  ) {
    listSortByByTab.IN_TRANSIT = sortInTransitRaw;
  }
  const sortCompletedRaw = searchParams.get(
    TRIP_VIEW_LIST_QUERY_PARAMS.sortCompleted,
  );
  if (
    sortCompletedRaw != null &&
    isSortOptionForTab("COMPLETED", sortCompletedRaw)
  ) {
    listSortByByTab.COMPLETED = sortCompletedRaw;
  }
  if (Object.keys(listSortByByTab).length > 0) {
    result.listSortByByTab = listSortByByTab;
  }

  const isCollapsed = parseListCollapsedParam(
    searchParams.get(TRIP_VIEW_LIST_QUERY_PARAMS.listCollapsed),
  );
  if (isCollapsed != null) {
    result.isCollapsed = isCollapsed;
  }

  return result;
}

function formatDayQueryParam(day: CalendarDay): string {
  const month = String(day.month + 1).padStart(2, "0");
  const dayOfMonth = String(day.day).padStart(2, "0");
  return `${day.year}-${month}-${dayOfMonth}`;
}

function serializeVisitStatusCsv(
  statuses: readonly VisitStatus[],
): string | undefined {
  if (isTripCompletedStatusFilterAllSelected(statuses)) {
    return undefined;
  }
  return statuses.join(",");
}

/**
 * Returns true when any v1 trip list query param is present (share / deep link).
 */
export function hasTripViewListQueryParams(
  searchParams: URLSearchParams,
): boolean {
  return (
    TRIP_VIEW_LIST_QUERY_PARAM_NAMES.some((name) => searchParams.has(name)) ||
    searchParams.has(LEGACY_UPCOMING_STATUS_QUERY_PARAM)
  );
}

/**
 * Builds URL search params for the current trip list UI state. Omits params that
 * match product defaults (all statuses, all destinations, default sort, expanded,
 * IN_TRANSIT tab). Always includes `day` when `activeCalendarDay` is set.
 */
export function serializeTripViewListStateToSearchParams(
  state: TripViewListQuerySerializeInput,
): URLSearchParams {
  const params = new URLSearchParams();

  if (state.stageTab === "COMPLETED") {
    params.set(TRIP_VIEW_LIST_QUERY_PARAMS.tab, state.stageTab);
  }

  if (state.activeCalendarDay != null) {
    params.set(
      TRIP_VIEW_LIST_QUERY_PARAMS.day,
      formatDayQueryParam(state.activeCalendarDay),
    );
  }

  if (state.selectedDestinationClusterId != null) {
    params.set(
      TRIP_VIEW_LIST_QUERY_PARAMS.dest,
      state.selectedDestinationClusterId,
    );
  }

  const trimmedQuery = state.searchQuery.trim();
  if (trimmedQuery.length > 0) {
    params.set(TRIP_VIEW_LIST_QUERY_PARAMS.q, trimmedQuery);
  }

  const completedStatusCsv = serializeVisitStatusCsv(
    state.selectedCompletedStatuses,
  );
  if (completedStatusCsv != null) {
    params.set(TRIP_VIEW_LIST_QUERY_PARAMS.completedStatus, completedStatusCsv);
  }

  const upcomingStatusCsv = serializeVisitStatusCsv(
    state.selectedNextDeliveryStatuses,
  );
  if (upcomingStatusCsv != null) {
    params.set(TRIP_VIEW_LIST_QUERY_PARAMS.upcomingStatus, upcomingStatusCsv);
  }

  const inTransitSort = state.listSortByByTab.IN_TRANSIT;
  if (
    isSortOptionForTab("IN_TRANSIT", inTransitSort) &&
    inTransitSort !== getDefaultTripListSortOption("IN_TRANSIT")
  ) {
    params.set(TRIP_VIEW_LIST_QUERY_PARAMS.sortInTransit, inTransitSort);
  }

  const completedSort = state.listSortByByTab.COMPLETED;
  if (
    isSortOptionForTab("COMPLETED", completedSort) &&
    completedSort !== getDefaultTripListSortOption("COMPLETED")
  ) {
    params.set(TRIP_VIEW_LIST_QUERY_PARAMS.sortCompleted, completedSort);
  }

  if (state.isCollapsed) {
    params.set(TRIP_VIEW_LIST_QUERY_PARAMS.listCollapsed, "1");
  }

  return params;
}

/** Query string for Towers Trip tab/sidebar browse landing (viewer-local today). */
export function serializeTripViewBrowseLandingDaySearch(
  day: CalendarDay,
): string {
  const params = new URLSearchParams();
  params.set(TRIP_VIEW_LIST_QUERY_PARAMS.day, formatDayQueryParam(day));
  return params.toString();
}

/** Query string without leading `?`. */
export function serializeTripViewListStateToSearchString(
  state: TripViewListQuerySerializeInput,
): string {
  return serializeTripViewListStateToSearchParams(state).toString();
}
